import {
	contentCommonPartNames,
	ContentType,
} from "../../../../schemas/questions/contnets/common-schemas";
import {
	IMultipleChoiceContent,
	IMultipleChoiceUserAns,
	IRMultipleChoiceContent,
	MCContentDesignStructure,
} from "../../../../schemas/questions/contnets/multiple-choice/schema";
import { QuestionContent } from "../class";

class MultipleChoice
	extends QuestionContent<IMultipleChoiceUserAns, IMultipleChoiceUserAns>
	implements IMultipleChoiceContent {
	static getEmptyContent(
		designStructure?: MCContentDesignStructure
	): IMultipleChoiceContent {
		if (designStructure === MCContentDesignStructure.twoColumns) {
			return {
				type: ContentType.MultipleChoice,
				statement: {
					id: 1,
					text: "",
					textA: "",
					textB: "",
				},
				choices: [
					{
						id: 2,
						text:
							"A სვეტის უჯრაში მოცემული რაოდენობა მეტია B სვეტის უჯრაში მოცემულ რაოდენობაზე.",
					},
					{
						id: 3,
						text:
							"B სვეტის უჯრაში მოცემული რაოდენობა მეტია A სვეტის უჯრაში მოცემულ რაოდენობაზე.",
					},
					{
						id: 4,
						text:
							"A სვეტის უჯრაში მოცემული რაოდენობა B სვეტის უჯრაში მოცემული რაოდენობის ტოლია.",
					},
					{
						id: 5,
						text:
							"მოცემული ინფორმაცია საკმარისი არაა იმის დასადგენად, რომელი რაოდენობაა მეტი.",
					},
				],
				designStructure: MCContentDesignStructure.twoColumns,
			};
		}
		if (designStructure === MCContentDesignStructure.dataSufficiency) {
			return {
				type: ContentType.MultipleChoice,
				statement: {
					id: 1,
					text: "<br/>მოცემულია ორი პირობა:<br/>I. <br/>II. <br/>",
				},
				choices: [
					{
						id: 2,
						text: "I პირობა საკმარისია, II კი – არა.",
					},
					{
						id: 3,
						text: "II პირობა საკმარისია, I კი – არა.",
					},
					{
						id: 4,
						text:
							"I და II პირობა ერთად საკმარისია, ცალ-ცალკე არც ერთი არ არის საკმარისი.",
					},
					{
						id: 5,
						text:
							"საკმარისია ცალ-ცალკე როგორც I, ასევე, II პირობა.",
					},
					{
						id: 6,
						text: "მოცემული პირობები არ არის საკმარისი.",
					},
				],
				designStructure: MCContentDesignStructure.dataSufficiency,
			};
		}
		return {
			type: ContentType.MultipleChoice,
			statement: {
				id: 1,
				text: "",
			},
			choices: new Array(4).fill(0).map((e, i) => ({
				id: i + 1,
				text: "",
			})),
		};
	}

	type: IMultipleChoiceContent["type"];

	statement: IMultipleChoiceContent["statement"];
	choices: IMultipleChoiceContent["choices"];
	canSelectMultiple: IMultipleChoiceContent["canSelectMultiple"];
	disableShuffle?: IMultipleChoiceContent["disableShuffle"];
	designStructure?: IMultipleChoiceContent["designStructure"];

	constructor(content: IMultipleChoiceContent) {
		super();
		if (content.type !== ContentType.MultipleChoice) {
			throw new Error("not multiple choice");
		}
		const keys = [
			...contentCommonPartNames,
			"statement",
			"choices",
			"canSelectMultiple",
			"canSelectMultiple",
			"disableShuffle",
			"disableShuffle",
			"designStructure",
		];
		keys.forEach(fieldName => {
			if (content[fieldName] !== undefined) {
				this[fieldName] = content[fieldName];
			}
		});
	}

	getStripped() {
		const stripped: IRMultipleChoiceContent = {
			type: this.type,
			statement: this.statement,
			choices: this.choices.map(e => {
				const { score, ...rest } = e;
				return rest;
			}),
			canSelectMultiple: this.canSelectMultiple,
			disableShuffle: this.disableShuffle,
		};
		return stripped;
	}

	getAnswer() {
		if (this.canSelectMultiple) {
			return this.choices
				.filter(c => typeof c.score === "number" && c.score > 0)
				.map(c => c.id);
		}
		const choice = this.choices.find(
			c => typeof c.score === "number" && c.score > 0
		);
		if (!choice) {
			return null;
		}
		return choice.id;
	}

	getCorrectAnswerAsUserAnswer() {
		return this.getAnswer();
	}

	isAnsweredCorrectly(userAnswer: number | number[]) {
		const creditShare = this.getCreditShare(userAnswer);
		if (creditShare >= 1 - 1e-6) return true;
		const maxCredit = this.getMaxCredit();
		const myCredit = creditShare * maxCredit;
		if (this.allowPartialCredit) {
			if (this.minScoreForCredit === undefined) return myCredit > 0;
			return myCredit >= this.minScoreForCredit! - 1e-6;
		}
		return false;
	}

	getCreditShare(userAnswer: number | number[]): number {
		const answer = this.getAnswer();

		if (!this.canSelectMultiple) {
			return userAnswer === answer ? 1 : 0;
		}
		if (!Array.isArray(answer) || !Array.isArray(userAnswer)) {
			throw new Error(
				"when canSelectMultiple is set to true, answers must be multiple"
			);
		}

		let numCorrect = 0;

		for (let i = 0; i < userAnswer.length; i++) {
			if (answer.indexOf(userAnswer[i]) !== -1) {
				numCorrect++;
			}
		}

		return userAnswer.length !== 0 ? numCorrect / userAnswer.length : 0;
	}

	getMaxCredit() {
		if (!this.canSelectMultiple) {
			return this.choices.reduce(
				(max, current) => Math.max(max, current.score || 0),
				0
			) as number;
		}
		return this.choices.reduce(
			(max, current) => max + (current.score || 0),
			0
		) as number;
	}

	getShortStat(separator = this.defaultSeparator) {
		if (this.designStructure === MCContentDesignStructure.twoColumns) {
			return [
				this.statement.text,
				this.statement.textA,
				this.statement.textB,
			]
				.filter(e => !!e)
				.join(separator);
		}
		return this.statement.text;
	}

	serialize(separator = this.defaultSeparator) {
		const statement = this.getShortStat(separator);
		const choices = this.choices.map(e => e.text).join(separator);
		return statement + separator + choices;
	}

	hasAnsweredFully(userAnswer?: IMultipleChoiceUserAns) {
		if (userAnswer === null || userAnswer === undefined) return false;
		return true;
	}
}

export default MultipleChoice;
